<template>
  <head>
    <meta charset="utf-8" />
    <title>NTSA | Bank Reconciliation System</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Comprehensive Bank Reconciliation System" name="description" />
    <meta content="" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <!-- Bootstrap and other CSS -->
    <link href="assets/libs/bootstrap-datepicker/css/bootstrap-datepicker.min.css" rel="stylesheet" type="text/css">
    <link href="assets/libs/dropzone/min/dropzone.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <NavigationBar/>

    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Start page title -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="page-title-box d-flex align-items-center justify-content-between">
                <div>
                  <h4 class="mb-0 font-size-18">Bank Reconciliation System</h4>
                  <p class="text-muted mb-0 mt-1">Real-time, Daily, Weekly, Monthly & Annual Reconciliations</p>
                </div>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Dashboard</a></li>
                    <li class="breadcrumb-item active">Bank Reconciliation</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Real-time Status Alert -->
          <div class="row mb-3" v-if="realTimeEnabled">
            <div class="col-12">
              <div class="alert alert-success d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="spinner-grow spinner-grow-sm me-2" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <strong>Real-time Sync Active</strong>
                  <span class="ms-2">- Last sync: {{ formatTime(lastSyncTime) }} | Next sync in: {{ nextSyncCountdown }}s</span>
                </div>
                <button class="btn btn-sm btn-outline-success" @click="toggleRealTimeSync">
                  <i class="mdi mdi-pause"></i> Pause Real-time
                </button>
              </div>
            </div>
          </div>

          <!-- Reconciliation Type Selector -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <h5 class="card-title mb-3">Select Reconciliation Type</h5>
                  <div class="btn-group" role="group">
                    <button
                        v-for="type in reconciliationTypes"
                        :key="type.value"
                        type="button"
                        :class="['btn', selectedReconciliationType === type.value ? 'btn-primary' : 'btn-outline-primary']"
                        @click="selectReconciliationType(type.value)"
                    >
                      <i :class="type.icon"></i> {{ type.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Stats Row -->
          <div class="row mb-4">
            <div class="col-md-3">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Total Reconciliations</p>
                      <h4 class="mb-0">{{ totalReconciliations }}</h4>
                    </div>
                    <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-primary">
                      <span class="avatar-title">
                        <i class="bx bx-check-circle font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Today's Matches</p>
                      <h4 class="mb-0">{{ todaysMatches }}</h4>
                    </div>
                    <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-success">
                      <span class="avatar-title">
                        <i class="bx bx-trending-up font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Pending Items</p>
                      <h4 class="mb-0">{{ pendingItems }}</h4>
                    </div>
                    <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-warning">
                      <span class="avatar-title">
                        <i class="bx bx-time-five font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Amount Reconciled</p>
                      <h4 class="mb-0">KES {{ formatPrice(totalReconciledAmount) }}</h4>
                    </div>
                    <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-info">
                      <span class="avatar-title">
                        <i class="bx bx-money font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Reconciliation Tabs -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white">
                  <ul class="nav nav-tabs card-header-tabs">
                    <li class="nav-item">
                      <a
                          :class="['nav-link', activeTab === 'overview' ? 'active' : '']"
                          href="javascript: void(0);"
                          @click.prevent="activeTab = 'overview'"
                      >
                        <i class="mdi mdi-view-dashboard me-1"></i> Overview
                      </a>
                    </li>
                    <li class="nav-item">
                      <a
                          :class="['nav-link', activeTab === 'realtime' ? 'active' : '']"
                          href="javascript: void(0);"
                          @click.prevent="activeTab = 'realtime'"
                      >
                        <i class="mdi mdi-sync me-1"></i> Real-time
                        <span class="badge bg-success ms-1" v-if="realTimeEnabled">Active</span>
                      </a>
                    </li>
                    <li class="nav-item">
                      <a
                          :class="['nav-link', activeTab === 'scheduled' ? 'active' : '']"
                          href="javascript: void(0);"
                          @click.prevent="activeTab = 'scheduled'"
                      >
                        <i class="mdi mdi-calendar-clock me-1"></i> Scheduled
                      </a>
                    </li>
                    <li class="nav-item">
                      <a
                          :class="['nav-link', activeTab === 'history' ? 'active' : '']"
                          href="javascript: void(0);"
                          @click.prevent="activeTab = 'history'"
                      >
                        <i class="mdi mdi-history me-1"></i> History
                      </a>
                    </li>
                    <li class="nav-item">
                      <a
                          :class="['nav-link', activeTab === 'settings' ? 'active' : '']"
                          href="javascript: void(0);"
                          @click.prevent="activeTab = 'settings'"
                      >
                        <i class="mdi mdi-cog me-1"></i> Settings
                      </a>
                    </li>
                  </ul>
                </div>

                <div class="card-body">
                  <!-- Overview Tab -->
                  <div v-show="activeTab === 'overview'">
                    <div class="row">
                      <div class="col-md-8">
                        <h5 class="mb-3">Reconciliation Progress</h5>
                        <canvas id="reconciliationChart" height="100"></canvas>
                      </div>
                      <div class="col-md-4">
                        <h5 class="mb-3">Quick Actions</h5>
                        <div class="d-grid gap-2">
                          <button class="btn btn-primary" @click="startManualReconciliation">
                            <i class="mdi mdi-play me-1"></i> Start Manual Reconciliation
                          </button>
                          <button class="btn btn-outline-primary" @click="showUploadModal = true">
                            <i class="mdi mdi-upload me-1"></i> Upload Bank Statement
                          </button>
                          <button class="btn btn-outline-primary" @click="downloadReport">
                            <i class="mdi mdi-download me-1"></i> Download Report
                          </button>
                          <button class="btn btn-outline-primary" @click="showScheduleModal = true">
                            <i class="mdi mdi-calendar-plus me-1"></i> Schedule Reconciliation
                          </button>
                        </div>

                        <div class="mt-4">
                          <h6>Next Scheduled Runs</h6>
                          <ul class="list-unstyled">
                            <li v-for="schedule in upcomingSchedules" :key="schedule.id" class="mb-2">
                              <div class="d-flex justify-content-between align-items-center">
                                <span>
                                  <i :class="schedule.icon"></i> {{ schedule.type }}
                                </span>
                                <small class="text-muted">{{ schedule.nextRun }}</small>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Real-time Tab -->
                  <div v-show="activeTab === 'realtime'">
                    <div class="row">
                      <div class="col-12">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Real-time Reconciliation Monitor</h5>
                          <div>
                            <button
                                :class="['btn', realTimeEnabled ? 'btn-success' : 'btn-outline-success']"
                                @click="toggleRealTimeSync"
                            >
                              <i :class="['mdi', realTimeEnabled ? 'mdi-pause' : 'mdi-play']"></i>
                              {{ realTimeEnabled ? 'Pause' : 'Start' }} Real-time Sync
                            </button>
                          </div>
                        </div>

                        <!-- Real-time Transaction Stream -->
                        <div class="card bg-light">
                          <div class="card-body">
                            <h6 class="mb-3">Live Transaction Stream</h6>
                            <div class="transaction-stream" style="max-height: 400px; overflow-y: auto;">
                              <div
                                  v-for="transaction in realtimeTransactions"
                                  :key="transaction.id"
                                  class="transaction-item mb-2 p-3 bg-white rounded"
                              >
                                <div class="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{{ transaction.reference }}</strong>
                                    <p class="mb-0 text-muted small">{{ transaction.description }}</p>
                                  </div>
                                  <div class="text-end">
                                    <div class="fw-bold">KES {{ formatPrice(transaction.amount) }}</div>
                                    <span :class="['badge', getStatusBadgeClass(transaction.status)]">
                                      {{ transaction.status }}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div v-if="realtimeTransactions.length === 0" class="text-center py-5 text-muted">
                                <i class="mdi mdi-information-outline fs-1"></i>
                                <p>No real-time transactions to display</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Scheduled Tab -->
                  <div v-show="activeTab === 'scheduled'">
                    <div class="row">
                      <div class="col-12">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Scheduled Reconciliations</h5>
                          <button class="btn btn-primary" @click="showScheduleModal = true">
                            <i class="mdi mdi-plus me-1"></i> Add Schedule
                          </button>
                        </div>

                        <div class="table-responsive">
                          <table class="table table-hover">
                            <thead>
                            <tr>
                              <th>Type</th>
                              <th>Frequency</th>
                              <th>Next Run</th>
                              <th>Last Run</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="schedule in schedules" :key="schedule.id">
                              <td>
                                <i :class="schedule.icon"></i> {{ schedule.type }}
                              </td>
                              <td>{{ schedule.frequency }}</td>
                              <td>{{ formatDateTime(schedule.nextRun) }}</td>
                              <td>{{ formatDateTime(schedule.lastRun) }}</td>
                              <td>
                                  <span :class="['badge', schedule.enabled ? 'bg-success' : 'bg-secondary']">
                                    {{ schedule.enabled ? 'Active' : 'Inactive' }}
                                  </span>
                              </td>
                              <td>
                                <div class="btn-group btn-group-sm">
                                  <button
                                      class="btn btn-outline-primary"
                                      @click="toggleSchedule(schedule)"
                                  >
                                    <i :class="['mdi', schedule.enabled ? 'mdi-pause' : 'mdi-play']"></i>
                                  </button>
                                  <button
                                      class="btn btn-outline-primary"
                                      @click="editSchedule(schedule)"
                                  >
                                    <i class="mdi mdi-pencil"></i>
                                  </button>
                                  <button
                                      class="btn btn-outline-danger"
                                      @click="deleteSchedule(schedule)"
                                  >
                                    <i class="mdi mdi-delete"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- History Tab -->
                  <div v-show="activeTab === 'history'">
                    <div class="row mb-3">
                      <div class="col-md-6">
                        <div class="search-box">
                          <div class="position-relative">
                            <input
                                type="text"
                                class="form-control rounded bg-light border-0"
                                placeholder="Search reconciliation history..."
                                v-model="searchTerm"
                            >
                            <i class="mdi mdi-magnify search-icon"></i>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="d-flex gap-2 justify-content-end">
                          <select class="form-select w-auto" v-model="filterType">
                            <option value="">All Types</option>
                            <option value="realtime">Real-time</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                          </select>
                          <select class="form-select w-auto" v-model="filterStatus">
                            <option value="">All Status</option>
                            <option value="completed">Completed</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div class="table-responsive">
                      <table class="table table-centered table-hover">
                        <thead>
                        <tr class="table-light">
                          <th>Date & Time</th>
                          <th>Type</th>
                          <th>Period</th>
                          <th>Transactions</th>
                          <th>Matched</th>
                          <th>Unmatched</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="item in filteredHistory" :key="item.id">
                          <td>
                            <div class="d-flex flex-column">
                              <span class="fw-medium">{{ formatDate(item.dateCreated) }}</span>
                              <small class="text-muted">{{ formatTime(item.dateCreated) }}</small>
                            </div>
                          </td>
                          <td>
                              <span class="badge" :class="getReconciliationTypeBadgeClass(item.type)">
                                {{ item.type }}
                              </span>
                          </td>
                          <td>{{ item.period }}</td>
                          <td>{{ item.totalTransactions }}</td>
                          <td class="text-success">{{ item.matchedTransactions }}</td>
                          <td class="text-danger">{{ item.unmatchedTransactions }}</td>
                          <td>KES {{ formatPrice(item.totalAmount) }}</td>
                          <td>
                              <span :class="['badge', getStatusBadgeClass(item.status)]">
                                {{ item.status }}
                              </span>
                          </td>
                          <td>
                            <div class="dropdown">
                              <a class="btn btn-sm btn-icon btn-light" href="javascript: void(0);" role="button" data-bs-toggle="dropdown">
                                <i class="mdi mdi-dots-vertical"></i>
                              </a>
                              <ul class="dropdown-menu dropdown-menu-end">
                                <li>
                                  <a class="dropdown-item" href="javascript: void(0);" @click.prevent="viewDetails(item)">
                                    <i class="mdi mdi-eye-outline me-2"></i>View Details
                                  </a>
                                </li>
                                <li>
                                  <a class="dropdown-item" href="javascript: void(0);" @click.prevent="downloadItemReport(item)">
                                    <i class="mdi mdi-download me-2"></i>Download Report
                                  </a>
                                </li>
                                <li>
                                  <a class="dropdown-item" href="javascript: void(0);" @click.prevent="rerunReconciliation(item)">
                                    <i class="mdi mdi-refresh me-2"></i>Re-run
                                  </a>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Settings Tab -->
                  <div v-show="activeTab === 'settings'">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="card">
                          <div class="card-body">
                            <h5 class="card-title mb-4">Real-time Settings</h5>
                            <form>
                              <div class="mb-3">
                                <label class="form-label">Sync Interval (seconds)</label>
                                <input
                                    type="number"
                                    class="form-control"
                                    v-model="settings.realtime.interval"
                                    min="5"
                                    max="300"
                                >
                              </div>
                              <div class="mb-3">
                                <label class="form-label">Auto-match Threshold (%)</label>
                                <input
                                    type="number"
                                    class="form-control"
                                    v-model="settings.realtime.matchThreshold"
                                    min="50"
                                    max="100"
                                >
                              </div>
                              <div class="form-check mb-3">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    id="autoApprove"
                                    v-model="settings.realtime.autoApprove"
                                >
                                <label class="form-check-label" for="autoApprove">
                                  Auto-approve matched transactions
                                </label>
                              </div>
                              <button type="submit" class="btn btn-primary" @click.prevent="saveSettings">
                                Save Real-time Settings
                              </button>
                            </form>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="card">
                          <div class="card-body">
                            <h5 class="card-title mb-4">Scheduled Reconciliation Defaults</h5>
                            <form>
                              <div class="mb-3">
                                <label class="form-label">End of Day Time</label>
                                <input
                                    type="time"
                                    class="form-control"
                                    v-model="settings.scheduled.endOfDayTime"
                                >
                              </div>
                              <div class="mb-3">
                                <label class="form-label">Weekly Run Day</label>
                                <select class="form-select" v-model="settings.scheduled.weeklyDay">
                                  <option value="0">Sunday</option>
                                  <option value="1">Monday</option>
                                  <option value="2">Tuesday</option>
                                  <option value="3">Wednesday</option>
                                  <option value="4">Thursday</option>
                                  <option value="5">Friday</option>
                                  <option value="6">Saturday</option>
                                </select>
                              </div>
                              <div class="mb-3">
                                <label class="form-label">Monthly Run Day</label>
                                <input
                                    type="number"
                                    class="form-control"
                                    v-model="settings.scheduled.monthlyDay"
                                    min="1"
                                    max="31"
                                >
                              </div>
                              <button type="submit" class="btn btn-primary" @click.prevent="saveSettings">
                                Save Schedule Settings
                              </button>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Schedule Modal -->
          <div class="modal fade" id="scheduleModal" tabindex="-1" v-if="showScheduleModal">
            <div class="modal-dialog">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Schedule Reconciliation</h5>
                  <button type="button" class="btn-close" @click="showScheduleModal = false"></button>
                </div>
                <div class="modal-body">
                  <form>
                    <div class="mb-3">
                      <label class="form-label">Reconciliation Type</label>
                      <select class="form-select" v-model="newSchedule.type">
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                      </select>
                    </div>
                    <div class="mb-3" v-if="newSchedule.type === 'daily'">
                      <label class="form-label">Run Time</label>
                      <input type="time" class="form-control" v-model="newSchedule.runTime">
                    </div>
                    <div class="mb-3" v-if="newSchedule.type === 'weekly'">
                      <label class="form-label">Day of Week</label>
                      <select class="form-select" v-model="newSchedule.dayOfWeek">
                        <option value="0">Sunday</option>
                        <option value="1">Monday</option>
                        <option value="2">Tuesday</option>
                        <option value="3">Wednesday</option>
                        <option value="4">Thursday</option>
                        <option value="5">Friday</option>
                        <option value="6">Saturday</option>
                      </select>
                    </div>
                    <div class="mb-3" v-if="newSchedule.type === 'monthly'">
                      <label class="form-label">Day of Month</label>
                      <input type="number" class="form-control" v-model="newSchedule.dayOfMonth" min="1" max="31">
                    </div>
                    <div class="form-check mb-3">
                      <input class="form-check-input" type="checkbox" id="enableSchedule" v-model="newSchedule.enabled">
                      <label class="form-check-label" for="enableSchedule">
                        Enable this schedule
                      </label>
                    </div>
                  </form>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" @click="showScheduleModal = false">Cancel</button>
                  <button type="button" class="btn btn-primary" @click="createSchedule">Create Schedule</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Upload Modal (keeping your existing FileUploadPreview) -->
          <FileUploadPreview
              @upload-complete="getUploadedTransactions"
              @refresh-uploads="getUploadedTransactions"
          />

        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- Footer -->
      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">

            </div>
            <div class="col-sm-6">
              <div class="text-sm-end d-none d-sm-block">

              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import {paysol, execute, execute2, biller, getRight, base} from "@/api";
import moment from "moment/moment";
import FileUploadPreview from '@/components/recon/UploadWithPreview.vue';
import Chart from 'chart.js/auto';

export default {
  name: "BankReconciliation",
  components: {
    NavigationBar,
    FileUploadPreview
  },
  data() {
    return {
      // Existing data
      loading: false,
      uploads: [],
      banks: [],

      // New reconciliation data
      activeTab: 'overview',
      selectedReconciliationType: 'realtime',
      realTimeEnabled: false,
      searchTerm: '',
      filterType: '',
      filterStatus: '',
      showScheduleModal: false,
      showUploadModal: false,

      // Real-time sync data
      lastSyncTime: new Date(),
      nextSyncCountdown: 30,
      syncInterval: null,
      countdownInterval: null,

      // Statistics
      totalReconciliations: 0,
      todaysMatches: 0,
      pendingItems: 0,
      totalReconciledAmount: 0,

      // Reconciliation types
      reconciliationTypes: [
        { value: 'realtime', label: 'Real-time', icon: 'mdi mdi-sync' },
        { value: 'daily', label: 'End of Day', icon: 'mdi mdi-calendar-today' },
        { value: 'weekly', label: 'Weekly', icon: 'mdi mdi-calendar-week' },
        { value: 'monthly', label: 'Monthly', icon: 'mdi mdi-calendar-month' },
        { value: 'annual', label: 'Annual', icon: 'mdi mdi-calendar' }
      ],

      // Real-time transactions
      realtimeTransactions: [],

      // Schedules
      schedules: [
        {
          id: 1,
          type: 'Daily',
          icon: 'mdi mdi-calendar-today',
          frequency: 'Every day at 23:30',
          nextRun: new Date(Date.now() + 86400000),
          lastRun: new Date(Date.now() - 86400000),
          enabled: true
        },
        {
          id: 2,
          type: 'Weekly',
          icon: 'mdi mdi-calendar-week',
          frequency: 'Every Sunday at 00:00',
          nextRun: new Date(Date.now() + 604800000),
          lastRun: new Date(Date.now() - 604800000),
          enabled: true
        },
        {
          id: 3,
          type: 'Monthly',
          icon: 'mdi mdi-calendar-month',
          frequency: 'Last day of month at 23:59',
          nextRun: new Date(Date.now() + 2592000000),
          lastRun: new Date(Date.now() - 2592000000),
          enabled: false
        }
      ],

      upcomingSchedules: [
        { id: 1, type: 'End of Day', icon: 'mdi mdi-clock-outline', nextRun: 'Today at 23:30' },
        { id: 2, type: 'Weekly', icon: 'mdi mdi-calendar-week', nextRun: 'Sunday at 00:00' },
        { id: 3, type: 'Monthly', icon: 'mdi mdi-calendar-month', nextRun: 'Dec 31 at 23:59' }
      ],

      // History data
      reconciliationHistory: [],

      // New schedule form
      newSchedule: {
        type: 'daily',
        runTime: '23:30',
        dayOfWeek: '0',
        dayOfMonth: '31',
        enabled: true
      },

      // Settings
      settings: {
        realtime: {
          interval: 30,
          matchThreshold: 90,
          autoApprove: true
        },
        scheduled: {
          endOfDayTime: '23:30',
          weeklyDay: '0',
          monthlyDay: '31'
        }
      },

      // Chart instance
      reconciliationChart: null
    };
  },

  mounted() {
    this.uploads.splice(0);
    this.getUploadedTransactions();
    this.loadReconciliationHistory();
    this.loadStatistics();
    this.menuAccess();
    this.getBanks();
    this.initializeChart();

    // Start real-time sync if it was previously enabled
    if (localStorage.getItem('realTimeEnabled') === 'true') {
      this.startRealTimeSync();
    }
  },

  beforeDestroy() {
    // Clean up intervals
    if (this.syncInterval) clearInterval(this.syncInterval);
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  },

  computed: {
    filteredHistory() {
      let filtered = this.reconciliationHistory;

      if (this.searchTerm) {
        filtered = filtered.filter(item =>
            item.reference?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            item.type?.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
      }

      if (this.filterType) {
        filtered = filtered.filter(item => item.type === this.filterType);
      }

      if (this.filterStatus) {
        filtered = filtered.filter(item => item.status === this.filterStatus);
      }

      return filtered;
    }
  },

  methods: {
    getRight,

    // Existing methods
    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Recon");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, biller)
          .then((res) => {
            // Handle response
          })
          .catch((e) => {
            // Handle error
          });
    },

    formatDate(dateTimeStr) {
      if (!dateTimeStr) return 'N/A';
      const date = new Date(dateTimeStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    formatTime(dateTimeStr) {
      if (!dateTimeStr) return '';
      const date = new Date(dateTimeStr);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    },

    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return 'N/A';
      return this.formatDate(dateTimeStr) + ' ' + this.formatTime(dateTimeStr);
    },

    formatPrice(value) {
      if (!value) return '0.00';
      let val = (value / 1).toFixed(2).replace(".", ",");
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },

    getUploadedTransactions() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getUploads");

      execute2(data, paysol)
          .then((res) => {
            if (res.data.success) {
              this.loading = false;
              this.uploads = res.data.data.uploads;
            } else {
              this.loading = false;
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error("Error fetching uploads:", e);
          });
    },

    getBanks() {
      const data = new FormData();
      data.append("function", "getBanks");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.banks.splice(0);
              this.banks = res.data.data.banks;
            }
          })
          .catch((e) => {
            console.error("Error fetching banks:", e);
          });
    },

    // New reconciliation methods
    selectReconciliationType(type) {
      this.selectedReconciliationType = type;
      if (type === 'realtime' && !this.realTimeEnabled) {
        this.startRealTimeSync();
      }
    },

    toggleRealTimeSync() {
      if (this.realTimeEnabled) {
        this.stopRealTimeSync();
      } else {
        this.startRealTimeSync();
      }
    },

    startRealTimeSync() {
      this.realTimeEnabled = true;
      localStorage.setItem('realTimeEnabled', 'true');

      // Start sync interval
      this.syncInterval = setInterval(() => {
        this.performRealTimeSync();
      }, this.settings.realtime.interval * 1000);

      // Start countdown interval
      this.countdownInterval = setInterval(() => {
        this.nextSyncCountdown--;
        if (this.nextSyncCountdown <= 0) {
          this.nextSyncCountdown = this.settings.realtime.interval;
        }
      }, 1000);

      // Perform initial sync
      this.performRealTimeSync();
    },

    stopRealTimeSync() {
      this.realTimeEnabled = false;
      localStorage.setItem('realTimeEnabled', 'false');

      if (this.syncInterval) {
        clearInterval(this.syncInterval);
        this.syncInterval = null;
      }

      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    performRealTimeSync() {
      this.lastSyncTime = new Date();

      // Simulate fetching real-time transactions
      const data = new FormData();
      data.append("function", "getRealTimeTransactions");
      data.append("lastSync", this.lastSyncTime.toISOString());

      // For demo purposes, simulating real-time transactions
      this.simulateRealTimeTransactions();
    },

    simulateRealTimeTransactions() {
      // Simulate new transactions coming in
      const newTransaction = {
        id: Date.now(),
        reference: `TRX${Date.now()}`,
        description: `Payment from Customer ${Math.floor(Math.random() * 1000)}`,
        amount: Math.floor(Math.random() * 100000) + 1000,
        status: Math.random() > 0.8 ? 'unmatched' : 'matched',
        timestamp: new Date()
      };

      this.realtimeTransactions.unshift(newTransaction);

      // Keep only last 50 transactions
      if (this.realtimeTransactions.length > 50) {
        this.realtimeTransactions.pop();
      }

      // Update statistics
      if (newTransaction.status === 'matched') {
        this.todaysMatches++;
        this.totalReconciledAmount += newTransaction.amount;
      } else {
        this.pendingItems++;
      }
    },

    startManualReconciliation() {
      const data = new FormData();
      data.append("function", "startManualReconciliation");
      data.append("type", this.selectedReconciliationType);

      this.loading = true;
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.$toast.success('Manual reconciliation started successfully');
              this.loadReconciliationHistory();
            }
            this.loading = false;
          })
          .catch((e) => {
            this.loading = false;
            this.$toast.error('Failed to start reconciliation');
          });
    },

    loadReconciliationHistory() {
      const data = new FormData();
      data.append("function", "getReconciliationHistory");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.reconciliationHistory = res.data.data.history || [];
            }
          })
          .catch((e) => {
            console.error("Error loading history:", e);
          });

      // Simulate history data for demo
      this.reconciliationHistory = [
        {
          id: 1,
          dateCreated: new Date(),
          type: 'daily',
          period: '2024-03-20',
          totalTransactions: 150,
          matchedTransactions: 145,
          unmatchedTransactions: 5,
          totalAmount: 2500000,
          status: 'completed'
        },
        {
          id: 2,
          dateCreated: new Date(Date.now() - 86400000),
          type: 'realtime',
          period: '2024-03-19',
          totalTransactions: 89,
          matchedTransactions: 87,
          unmatchedTransactions: 2,
          totalAmount: 1200000,
          status: 'completed'
        }
      ];
    },

    loadStatistics() {
      const data = new FormData();
      data.append("function", "getReconciliationStats");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.totalReconciliations = res.data.data.totalReconciliations || 0;
              this.todaysMatches = res.data.data.todaysMatches || 0;
              this.pendingItems = res.data.data.pendingItems || 0;
              this.totalReconciledAmount = res.data.data.totalAmount || 0;
            }
          })
          .catch((e) => {
            console.error("Error loading statistics:", e);
          });

      // Simulate statistics for demo
      this.totalReconciliations = 247;
      this.todaysMatches = 43;
      this.pendingItems = 7;
      this.totalReconciledAmount = 4567890;
    },

    initializeChart() {
      const ctx = document.getElementById('reconciliationChart');
      if (ctx) {
        this.reconciliationChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            datasets: [{
              label: 'Matched',
              data: [65, 78, 82, 91, 87, 94, 89],
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              tension: 0.1
            }, {
              label: 'Unmatched',
              data: [5, 3, 8, 2, 4, 1, 3],
              borderColor: 'rgb(255, 99, 132)',
              backgroundColor: 'rgba(255, 99, 132, 0.2)',
              tension: 0.1
            }]
          },
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: 'top',
              },
              title: {
                display: true,
                text: 'Weekly Reconciliation Trend'
              }
            },
            scales: {
              y: {
                beginAtZero: true
              }
            }
          }
        });
      }
    },

    getStatusBadgeClass(status) {
      const classes = {
        'matched': 'bg-success',
        'unmatched': 'bg-danger',
        'pending': 'bg-warning',
        'completed': 'bg-success',
        'failed': 'bg-danger',
        'in-progress': 'bg-info'
      };
      return classes[status] || 'bg-secondary';
    },

    getReconciliationTypeBadgeClass(type) {
      const classes = {
        'realtime': 'bg-info',
        'daily': 'bg-primary',
        'weekly': 'bg-success',
        'monthly': 'bg-warning',
        'annual': 'bg-danger'
      };
      return classes[type] || 'bg-secondary';
    },

    createSchedule() {
      const data = new FormData();
      data.append("function", "createSchedule");
      data.append("schedule", JSON.stringify(this.newSchedule));

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.$toast.success('Schedule created successfully');
              this.showScheduleModal = false;
              this.loadSchedules();
            }
          })
          .catch((e) => {
            this.$toast.error('Failed to create schedule');
          });
    },

    toggleSchedule(schedule) {
      schedule.enabled = !schedule.enabled;
      this.updateSchedule(schedule);
    },

    editSchedule(schedule) {
      this.newSchedule = { ...schedule };
      this.showScheduleModal = true;
    },

    deleteSchedule(schedule) {
      if (confirm('Are you sure you want to delete this schedule?')) {
        const data = new FormData();
        data.append("function", "deleteSchedule");
        data.append("scheduleId", schedule.id);

        execute(data, biller)
            .then((res) => {
              if (res.data.success) {
                this.$toast.success('Schedule deleted successfully');
                this.loadSchedules();
              }
            })
            .catch((e) => {
              this.$toast.error('Failed to delete schedule');
            });
      }
    },

    updateSchedule(schedule) {
      const data = new FormData();
      data.append("function", "updateSchedule");
      data.append("schedule", JSON.stringify(schedule));

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.$toast.success('Schedule updated successfully');
            }
          })
          .catch((e) => {
            this.$toast.error('Failed to update schedule');
          });
    },

    saveSettings() {
      localStorage.setItem('reconciliationSettings', JSON.stringify(this.settings));
      this.$toast.success('Settings saved successfully');

      // Restart real-time sync with new settings if enabled
      if (this.realTimeEnabled) {
        this.stopRealTimeSync();
        this.startRealTimeSync();
      }
    },

    viewDetails(item) {
      sessionStorage.setItem("reconCode", item.reconCode);
      this.$router.push('recon-details');
    },

    downloadReport() {
      const data = new FormData();
      data.append("function", "generateReconciliationReport");
      data.append("type", this.selectedReconciliationType);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              window.open(res.data.data.reportUrl, '_blank');
            }
          })
          .catch((e) => {
            this.$toast.error('Failed to generate report');
          });
    },

    downloadItemReport(item) {
      const data = new FormData();
      data.append("function", "downloadReconciliationReport");
      data.append("reconciliationId", item.id);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              window.open(res.data.data.reportUrl, '_blank');
            }
          })
          .catch((e) => {
            this.$toast.error('Failed to download report');
          });
    },

    rerunReconciliation(item) {
      if (confirm('Are you sure you want to re-run this reconciliation?')) {
        const data = new FormData();
        data.append("function", "rerunReconciliation");
        data.append("reconciliationId", item.id);

        this.loading = true;
        execute(data, biller)
            .then((res) => {
              if (res.data.success) {
                this.$toast.success('Reconciliation re-run started');
                this.loadReconciliationHistory();
              }
              this.loading = false;
            })
            .catch((e) => {
              this.loading = false;
              this.$toast.error('Failed to re-run reconciliation');
            });
      }
    },

    loadSchedules() {
      // Reload schedules from API
      const data = new FormData();
      data.append("function", "getSchedules");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.schedules = res.data.data.schedules;
            }
          })
          .catch((e) => {
            console.error("Error loading schedules:", e);
          });
    }
  }
};
</script>

<style scoped>
/* Enhanced styles for reconciliation module */
.table-responsive {
  overflow-x: auto;
}

.search-box {
  position: relative;
  max-width: 400px;
}

.search-box .form-control {
  padding-left: 40px;
  height: 38px;
}

.search-box .search-icon {
  position: absolute;
  left: 13px;
  top: 10px;
  font-size: 18px;
  color: #74788d;
}

.mini-stats-wid .mini-stat-icon {
  overflow: hidden;
  position: relative;
}

.mini-stats-wid .avatar-sm {
  height: 3rem;
  width: 3rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  font-weight: 500;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.transaction-stream {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
}

.transaction-item {
  border-left: 3px solid transparent;
  transition: all 0.3s ease;
}

.transaction-item:hover {
  border-left-color: #3b82f6;
  transform: translateX(5px);
}

.spinner-grow-sm {
  width: 0.75rem;
  height: 0.75rem;
}

/* Tab styling */
.nav-tabs .nav-link {
  color: #6c757d;
  border: none;
  padding: 0.75rem 1.25rem;
}

.nav-tabs .nav-link.active {
  color: #3b82f6;
  background-color: transparent;
  border-bottom: 2px solid #3b82f6;
}

.nav-tabs .nav-link:hover {
  color: #3b82f6;
  border-color: transparent;
}

/* Status badges */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 0.25rem;
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .btn-group {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .btn-group .btn {
    border-radius: 0.375rem !important;
    margin-bottom: 0.5rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }
}

/* Chart container */
#reconciliationChart {
  max-height: 300px;
}

/* Modal styling */
.modal-content {
  border-radius: 0.75rem;
}

.modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1.25rem;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}
</style>